/**
 * ArchivePanel — per-customer archive of FINALISED reports with version history,
 * retention status and one-click re-download of any in-retention version (and
 * its progress report). Also exposes the retention policy + a preview/purge of
 * expired material. Aged-out reports appear as lightweight tombstones (we keep
 * the memory of the engagement even after the reproducible snapshot is purged).
 */
import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Archive, Download, Settings2, Trash2, AlertTriangle, FileClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

async function downloadVersion(runId, fmt, version) {
  try {
    const q = version ? `?version=${version}` : "";
    const res = await fetch(`${API_BASE}/admin/assessments/runs/${runId}/export/${fmt}${q}`, { credentials: "include" });
    if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = m ? m[1] : `report.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch (err) { toast.error(err?.message || "Download failed"); }
}

export default function ArchivePanel({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [frozenMonths, setFrozenMonths] = useState(24);
  const [draftMonths, setDraftMonths] = useState(6);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/assessments/archive");
      setData(data);
      setFrozenMonths(data.settings.frozen_retention_months);
      setDraftMonths(data.settings.draft_retention_months);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load archive");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try {
      await api.put("/admin/assessments/retention/settings", {
        frozen_retention_months: Number(frozenMonths), draft_retention_months: Number(draftMonths),
      });
      toast.success("Retention policy updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const runPreview = async () => {
    try {
      const { data } = await api.get("/admin/assessments/retention/preview");
      setPreview(data);
    } catch { toast.error("Preview failed"); }
  };

  const purge = async () => {
    if (!window.confirm("Purge all expired finalised reports (kept as tombstones) and stale drafts? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/assessments/retention/purge");
      toast.success(`Purged ${data.expired_finalised_count} expired report(s) and ${data.stale_drafts_count} stale draft(s)`);
      setPreview(null); load();
    } catch { toast.error("Purge failed"); }
  };

  return (
    <div data-testid="assessment-archive-view">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onBack} className="text-zinc-600 hover:text-black inline-flex items-center gap-1 text-[13px]"><ArrowLeft size={14}/> Back to assessments</button>
      </div>

      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="ek-heading text-xl flex items-center gap-2"><Archive size={18}/> Client archive</h3>
          <p className="text-zinc-500 text-[13px] mt-1">Every finalised report by client, with version history and re-download. Reports are retained for {data?.settings?.frozen_retention_months ?? 24} months, then aged out to a tombstone.</p>
        </div>
        <button type="button" onClick={() => setShowSettings((s) => !s)} className="ek-btn ek-btn-outline text-[12px]" data-testid="retention-settings-toggle"><Settings2 size={13}/> Retention</button>
      </div>

      {showSettings && (
        <div className="bg-zinc-50 border border-zinc-200 p-4 mb-6" data-testid="retention-settings">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
            <label className="text-[12px]">
              <span className="text-zinc-600 block mb-1">Finalised report retention (months)</span>
              <input type="number" min="1" max="120" value={frozenMonths} onChange={(e) => setFrozenMonths(e.target.value)} className="w-full border border-zinc-300 px-2.5 py-1.5 text-[13px]" data-testid="retention-frozen-months" />
            </label>
            <label className="text-[12px]">
              <span className="text-zinc-600 block mb-1">Stale draft sweep (months idle)</span>
              <input type="number" min="1" max="120" value={draftMonths} onChange={(e) => setDraftMonths(e.target.value)} className="w-full border border-zinc-300 px-2.5 py-1.5 text-[13px]" data-testid="retention-draft-months" />
            </label>
          </div>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <button type="button" onClick={saveSettings} className="ek-btn ek-btn-primary text-[12px]" data-testid="retention-save">Save policy</button>
            <button type="button" onClick={runPreview} className="ek-btn ek-btn-outline text-[12px]" data-testid="retention-preview-btn"><FileClock size={13}/> Preview purge</button>
            <button type="button" onClick={purge} className="text-[12px] border border-red-300 text-red-700 hover:bg-red-50 px-3 py-1.5 flex items-center gap-1.5" data-testid="retention-purge-btn"><Trash2 size={13}/> Purge expired now</button>
          </div>
          {preview && (() => {
            const clear = (preview.expired_finalised_count + preview.stale_drafts_count) === 0;
            return (
              <div className="mt-3 text-[12px] text-zinc-700 bg-white border border-zinc-200 p-3" data-testid="retention-preview-result">
                {clear ? (
                  <div className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 size={13}/> Nothing eligible for purge — every report is within its retention window.</div>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5 text-amber-800"><AlertTriangle size={13}/> {preview.expired_finalised_count} expired report(s) and {preview.stale_drafts_count} stale draft(s) eligible for purge.</div>
                    {preview.expired_finalised.map((e) => <div key={e.run_id} className="text-[11px] text-zinc-500 mt-1">· {e.client_name} · {e.sub_brand} v{e.version} · finalised {fmtDate(e.finalised_at)}</div>)}
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {loading && <div className="text-[13px] text-zinc-500">Loading archive…</div>}
      {!loading && data && data.clients.length === 0 && (
        <div className="text-[13px] text-zinc-500 bg-zinc-50 border border-zinc-200 p-6 text-center" data-testid="archive-empty">No finalised reports yet. Finalise a completed assessment to add it to the archive.</div>
      )}

      <div className="space-y-5">
        {(data?.clients || []).map((c) => (
          <div key={c.client_name} className="bg-white border border-zinc-200" data-testid={`archive-client-${c.client_name}`}>
            <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between gap-3">
              <h4 className="ek-heading text-base text-black">{c.client_name}</h4>
              <span className="text-[11px] text-zinc-400">
                {c.engagements.length > 0
                  ? `${c.engagements.length} engagement${c.engagements.length > 1 ? "s" : ""}`
                  : "Archived only"}
                {c.tombstones.length > 0 ? ` · ${c.tombstones.length} aged out` : ""}
              </span>
            </div>
            <div className="p-5 space-y-4">
              {c.engagements.map((e) => (
                <div key={e.run_id} className="border border-zinc-100 p-4" data-testid={`archive-engagement-${e.run_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#7737F6]">{e.sub_brand}</span>
                        <span className="text-[11px] text-zinc-400">· {e.overall_weighted ?? "—"}/5 · {e.overall_level ?? "—"}</span>
                      </div>
                      <div className="text-[11.5px] text-zinc-500 mt-1">Current v{e.current_version} · finalised {fmtDate(e.finalised_at)}{e.finalised_by ? ` · ${e.finalised_by}` : ""}</div>
                    </div>
                    <div className={`text-[11px] px-2 py-1 ${e.expired ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {e.expired ? "Expired" : `Retained until ${fmtDate(e.expires_at)}`}
                    </div>
                  </div>

                  <div className="mt-3 space-y-1.5">
                    {e.versions.map((v) => (
                      <div key={v.version} className="flex items-center gap-2 flex-wrap text-[12px]" data-testid={`archive-version-${e.run_id}-${v.version}`}>
                        <span className={`font-medium ${v.current ? "text-[#7737F6]" : "text-zinc-500"}`}>v{v.version}{v.current ? " (current)" : ""}</span>
                        <span className="text-zinc-400 text-[11px]">{fmtDate(v.finalised_at)}</span>
                        <div className="flex items-center gap-1 ml-auto">
                          {["pdf", "execpack", "boardpaper"].map((fmt) => (
                            <button key={fmt} type="button" onClick={() => downloadVersion(e.run_id, fmt, v.current ? null : v.version)} className="text-[11px] text-[#7737F6] hover:text-[#5D28C7] inline-flex items-center gap-1 border border-zinc-200 px-2 py-1" data-testid={`archive-dl-${e.run_id}-${v.version}-${fmt}`}>
                              <Download size={11}/> {fmt === "pdf" ? "Full" : fmt === "execpack" ? "Exec" : "Board"}
                            </button>
                          ))}
                          {v.current && (
                            <button type="button" onClick={() => downloadVersion(e.run_id, "progress", null)} className="text-[11px] text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 border border-emerald-200 px-2 py-1" data-testid={`archive-dl-progress-${e.run_id}`}>
                              <Download size={11}/> Progress
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {c.tombstones.map((t, i) => (
                <div key={i} className="border border-dashed border-zinc-200 p-3 bg-zinc-50/50" data-testid={`archive-tombstone-${c.client_name}-${i}`}>
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                    <FileClock size={12}/> <span className="font-medium text-zinc-600">{t.framework_sub_brand}</span> · v{t.latest_version} · finalised {fmtDate(t.finalised_at)} · {t.overall_weighted ?? "—"}/5 {t.overall_level ? `· ${t.overall_level}` : ""}
                  </div>
                  <div className="text-[10.5px] text-zinc-400 mt-1">Aged out of retention — report purged {fmtDate(t.purged_at)}. Record kept for reference; not reproducible.</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
